import { useState, type FC } from 'react';
import { useIntl } from 'react-intl';
import { messages } from '../localization/messages.ts';

export interface AgentLogo {
  src: string;
  alt: string;
}

type LanguageChoice = {
  code: 'ka' | 'ru' | 'en';
  short: string;
  flag: string;
  label: (typeof messages)[keyof typeof messages];
  instruction: string;
};

const LANGUAGES: LanguageChoice[] = [
  {
    code: 'ka',
    short: 'KA',
    flag: '🇬🇪',
    label: messages.languageGeorgian,
    instruction: 'გთხოვთ, საუბრისა და ეკრანზე ნაჩვენები ტექსტის ენა ქართულზე შეცვალოთ.',
  },
  {
    code: 'ru',
    short: 'RU',
    flag: '🇷🇺',
    label: messages.languageRussian,
    instruction: 'Пожалуйста, переключите язык разговора и текущего экрана на русский.',
  },
  {
    code: 'en',
    short: 'EN',
    flag: '🇬🇧',
    label: messages.languageEnglish,
    instruction: 'Please switch the conversation and current screen to English.',
  },
];

function languageCode(locale: string): string {
  try {
    return new Intl.Locale(locale).language;
  } catch {
    return locale.split('-')[0]?.toLowerCase() ?? '';
  }
}

/** Compact brand row with accessible, conversational locale controls. */
export const StageHeader: FC<{
  logo?: AgentLogo;
  currentLocale: string;
  onRestart: (intent: string) => void;
  onLanguageSelect: (instruction: string) => void;
  disabled?: boolean;
}> = ({ logo, currentLocale, onRestart, onLanguageSelect, disabled = false }) => {
  const intl = useIntl();
  const [failedSrc, setFailedSrc] = useState<string>();
  const activeLanguage = languageCode(currentLocale);
  return (
    <header className="stage-header flex items-center pb-4 pt-logo-inset">
      <div className="mx-auto flex w-full max-w-container-content min-w-0 items-center justify-between gap-3">
        {logo?.src && logo.src !== failedSrc ? (
          <button
            type="button"
            aria-label={intl.formatMessage(messages.brandStartOver, { brandName: logo.alt })}
            disabled={disabled}
            onClick={() => onRestart(intl.formatMessage(messages.startOver))}
            className="flex h-12 w-12 flex-none items-center justify-center overflow-hidden rounded-full border border-border bg-card p-2 transition-colors hover:border-primary hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait disabled:opacity-60"
          >
            <img
              key={logo.src}
              src={logo.src}
              alt={logo.alt}
              width={64}
              height={64}
              className="h-full w-full object-contain"
              onError={() => setFailedSrc(logo.src)}
            />
          </button>
        ) : (
          <span aria-hidden="true" className="h-12 w-12 flex-none" />
        )}
        <div
          role="group"
          aria-label={intl.formatMessage(messages.languageSwitcherLabel)}
          className="flex flex-none items-center gap-0.5 rounded-full border border-border bg-card/80 p-1 shadow-sm backdrop-blur"
        >
          {LANGUAGES.map((language) => {
            const active = activeLanguage === language.code;
            return (
              <button
                key={language.code}
                type="button"
                lang={language.code}
                aria-label={intl.formatMessage(language.label)}
                aria-pressed={active}
                disabled={disabled}
                onClick={() => {
                  if (!active) {
                    onLanguageSelect(language.instruction);
                  }
                }}
                className={`flex h-9 min-w-11 items-center justify-center gap-1 rounded-full border px-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait disabled:opacity-50 motion-reduce:transition-none ${active ? 'border-primary bg-primary text-primary-foreground shadow-sm' : 'border-transparent text-muted-foreground hover:border-border hover:bg-accent-hover hover:text-foreground'}`}
              >
                <span aria-hidden="true" className="text-base leading-none">
                  {language.flag}
                </span>
                <span aria-hidden="true" className="text-[10px] font-semibold tracking-wide">
                  {language.short}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
};
